// Escala del color de la marca para el tema de @reparto/ui (--color-primary-50 … 950). El 600 es el color tal cual
// (botones, cabecera); los claros sirven de fondo y los oscuros de texto. Se arma en OKLCH (misma luminosidad
// percibida para cualquier tono) con topes de luminosidad: el texto en 700 o más oscuro siempre pasa AA sobre blanco,
// sea la marca verde petróleo o amarillo patito.

export const TONOS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;
export type Tono = (typeof TONOS)[number];

interface Oklch {
  l: number;
  c: number;
  h: number;
}

const lineal = (v: number) => {
  const c = v / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};
const gamma = (v: number) => (v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055);

export function hexAOklch(hex: string): Oklch | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = Number.parseInt(m[1]!, 16);
  const r = lineal((n >> 16) & 255);
  const g = lineal((n >> 8) & 255);
  const b = lineal(n & 255);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const mm = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * mm - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * mm + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * mm - 0.808675766 * s;
  return { l: L, c: Math.hypot(A, B), h: Math.atan2(B, A) };
}

function oklchARgb({ l, c, h }: Oklch): [number, number, number] {
  const a = c * Math.cos(h);
  const b = c * Math.sin(h);
  const l_ = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m_ = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s_ = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_,
    -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_,
    -0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_,
  ];
}

const dentro = (rgb: number[]) => rgb.every((v) => v >= -1e-4 && v <= 1 + 1e-4);

/** A hex de pantalla. Si el color se sale de sRGB, baja la saturación (no el tono) hasta que entre. */
export function oklchAHex(color: Oklch): string {
  let rgb = oklchARgb(color);
  if (!dentro(rgb)) {
    let bajo = 0;
    let alto = color.c;
    for (let i = 0; i < 20; i++) {
      const medio = (bajo + alto) / 2;
      if (dentro(oklchARgb({ ...color, c: medio }))) bajo = medio;
      else alto = medio;
    }
    rgb = oklchARgb({ ...color, c: bajo });
  }
  return `#${rgb
    .map((v) =>
      Math.round(Math.max(0, Math.min(1, gamma(Math.max(0, v)))) * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

// Claros: cuánto se acercan al blanco, la luminosidad mínima (fondos bien claros aunque la marca sea oscura) y cuánta
// saturación conservan. Oscuros: cuánto bajan, el tope (contraste del texto) y la saturación.
const CLAROS: Partial<Record<Tono, [number, number, number]>> = {
  50: [0.93, 0.97, 0.15],
  100: [0.86, 0.935, 0.3],
  200: [0.74, 0.88, 0.5],
  300: [0.56, 0, 0.7],
  400: [0.38, 0, 0.85],
  500: [0.2, 0, 0.95],
};
const OSCUROS: Partial<Record<Tono, [number, number, number]>> = {
  700: [0.86, 0.5, 0.95],
  800: [0.74, 0.42, 0.85],
  900: [0.63, 0.35, 0.75],
  950: [0.45, 0.26, 0.6],
};

export function escalaDeColor(hex: string): Record<Tono, string> | null {
  const base = hexAOklch(hex);
  if (!base) return null;
  const salida = {} as Record<Tono, string>;
  for (const t of TONOS) {
    const claro = CLAROS[t];
    const oscuro = OSCUROS[t];
    if (claro) {
      const [hacia, minimo, sat] = claro;
      salida[t] = oklchAHex({ l: Math.max(base.l + (1 - base.l) * hacia, minimo), c: base.c * sat, h: base.h });
    } else if (oscuro) {
      const [factor, tope, sat] = oscuro;
      salida[t] = oklchAHex({ l: Math.min(base.l * factor, tope), c: base.c * sat, h: base.h });
    } else salida[t] = `#${hex.replace('#', '').toLowerCase()}`;
  }
  return salida;
}

/** Fondos del modo oscuro (estilos.css): la tarjeta (`bg-white`) y la página (`bg-stone-50`). */
export const FONDOS_OSCUROS = ['#1c1917', '#0c0a09'] as const;

// Modo oscuro: los tonos claros pasan a ser fondos oscuros teñidos de la marca y los oscuros, textos claros. El 600
// sigue siendo el color de la marca (cabecera y botones); solo se aclara si es casi negro y se perdería en el fondo.
// Luminosidad de cada tono, en el orden de TONOS (el 600 no la usa).
const L_OSCURO = [0.25, 0.29, 0.35, 0.43, 0.53, 0.63, 0, 0.8, 0.86, 0.91, 0.95];

export function escalaOscura(hex: string): Record<Tono, string> | null {
  const base = hexAOklch(hex);
  if (!base) return null;
  const salida = {} as Record<Tono, string>;
  TONOS.forEach((t, i) => {
    salida[t] =
      t === 600
        ? base.l < 0.36
          ? oklchAHex({ ...base, l: 0.36 })
          : `#${hex.replace('#', '').toLowerCase()}`
        : oklchAHex({ l: L_OSCURO[i]!, c: Math.min(base.c, 0.14) * (t < 600 ? 0.45 : 0.55), h: base.h });
  });
  return salida;
}

/**
 * Las variables de la marca para la página: la escala clara y, en modo oscuro (`data-tema`), la oscura. `:root:root`
 * le gana a los valores neutros de estilos.css aunque esta hoja quede antes (la pone index.html al arrancar). El texto
 * sobre la marca (el que da la API) sirve en los dos modos: el 600 oscuro solo cambia si es casi negro, y sigue oscuro.
 */
export function cssDeMarca(hex: string, textoSobre: string): string {
  const claro = escalaDeColor(hex);
  const oscuro = escalaOscura(hex);
  if (!claro || !oscuro) return '';
  const variables = (e: Record<Tono, string>) =>
    `${TONOS.map((t) => `--color-primary-${t}:${e[t]}`).join(';')};--color-sobre-marca:${textoSobre}`;
  return `:root:root{${variables(claro)}}:root:root[data-tema=oscuro]{${variables(oscuro)}}`;
}

/** Contraste WCAG entre dos colores hex (1 a 21). */
export function contraste(a: string, b: string): number {
  const lum = (hex: string) => {
    const n = Number.parseInt(hex.replace('#', ''), 16);
    return 0.2126 * lineal((n >> 16) & 255) + 0.7152 * lineal((n >> 8) & 255) + 0.0722 * lineal(n & 255);
  };
  const [x, y] = [lum(a), lum(b)].sort((p, q) => q - p) as [number, number];
  return (x + 0.05) / (y + 0.05);
}
